import { useState } from 'react';
import GlowButton from '@/components/base/GlowButton';
import ModalOverlay from '@/components/base/ModalOverlay';

interface ShareModalProps {
  open: boolean;
  communityName: string;
  communityId: string;
  onClose: () => void;
}

export default function ShareModal({ open, communityName, communityId, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const pageUrl = `${window.location.origin}/community/${communityId}`;
  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedText = encodeURIComponent(`Check out "${communityName}" on TGDirectory!`);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
    } catch {
      const el = document.createElement('input');
      el.value = pageUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socialLinks = [
    { label: 'Telegram', icon: 'ri-telegram-fill', color: 'bg-[#0088CC]/10 border-[#0088CC]/25 text-[#0088CC] hover:bg-[#0088CC]/20', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}` },
    { label: 'Twitter / X', icon: 'ri-twitter-x-fill', color: 'bg-white/50 border-white/70 text-slate-700 hover:bg-white/80', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}` },
    { label: 'WhatsApp', icon: 'ri-whatsapp-line', color: 'bg-emerald-50/70 border-emerald-200/60 text-emerald-600 hover:bg-emerald-50', href: `https://wa.me/?text=${encodedText}%20${encodedUrl}` },
    { label: 'Reddit', icon: 'ri-reddit-line', color: 'bg-orange-50/70 border-orange-200/60 text-orange-600 hover:bg-orange-50', href: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}` },
    { label: 'LinkedIn', icon: 'ri-linkedin-fill', color: 'bg-sky-50/70 border-sky-200/60 text-sky-700 hover:bg-sky-50', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
    { label: 'Email', icon: 'ri-mail-line', color: 'bg-white/50 border-white/70 text-slate-600 hover:bg-white/80', href: `mailto:?subject=${encodedText}&body=${encodedUrl}` },
  ];

  return (
    <ModalOverlay open={open} onClose={onClose} zIndex={100}>
      <div className="w-full max-w-sm bg-white/75 backdrop-blur-2xl border border-white/90 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/60">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0088CC]/10 backdrop-blur-sm border border-[#0088CC]/20">
              <i className="ri-share-line text-[#0088CC] text-sm"></i>
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Share Community</h3>
              <p className="text-[11px] text-slate-400 truncate max-w-[180px]">{communityName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white/60 backdrop-blur-sm rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5">
          {/* Copy Link */}
          <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wider">Copy Link</p>
          <div className="flex items-center gap-2 bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl px-3 py-2.5 mb-5">
            <i className="ri-link text-slate-400 text-sm w-4 h-4 flex items-center justify-center shrink-0"></i>
            <span className="flex-1 text-xs text-slate-500 truncate">{pageUrl}</span>
            <GlowButton
              onClick={handleCopy as () => void}
              className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap ${copied ? 'opacity-80' : ''}`}
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs w-3 h-3 flex items-center justify-center`}></i>
              {copied ? 'Copied!' : 'Copy'}
            </GlowButton>
          </div>

          {/* Social Share */}
          <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wider">Share via</p>
          <div className="grid grid-cols-3 gap-2">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border backdrop-blur-sm transition-all cursor-pointer ${s.color}`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${s.icon} text-base`}></i>
                </span>
                <span className="text-[10px] font-medium whitespace-nowrap">{s.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
